import * as React from 'react';
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import {
  $getSelectionStyleValueForProperty,
  $patchStyleText,
  $setBlocksType,
} from '@lexical/selection';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $findMatchingParent,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import type {
  ElementFormatType,
  LexicalEditor,
  RangeSelection,
  TextFormatType,
} from 'lexical';
import {
  $clearSelectionFormatting,
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SIZE,
  getSelectedBlockType,
  getSelectedLinkUrl,
  getSelectedTopLevelElement,
  type BlockType,
} from '../../utils';
import { DEFAULT_BACKGROUND_COLOR, DEFAULT_TEXT_COLOR } from './constants';
import type {
  ToolbarFormattingController,
  ToolbarSelectionContext,
} from './types';

export function useToolbarFormatting(
  editor: LexicalEditor,
  isLinkEditorVisible: boolean
): {
  formatting: ToolbarFormattingController;
  selection: ToolbarSelectionContext;
} {
  const [blockType, setBlockType] = React.useState<BlockType>('paragraph');
  const [fontFamily, setFontFamily] = React.useState(DEFAULT_FONT_FAMILY);
  const [fontSize, setFontSize] = React.useState(
    DEFAULT_FONT_SIZE.replace('px', '')
  );
  const [isBold, setIsBold] = React.useState(false);
  const [isItalic, setIsItalic] = React.useState(false);
  const [isStrikethrough, setIsStrikethrough] = React.useState(false);
  const [isUnderline, setIsUnderline] = React.useState(false);
  const [textBackgroundColor, setTextBackgroundColor] = React.useState(
    DEFAULT_BACKGROUND_COLOR
  );
  const [textColor, setTextColor] = React.useState(DEFAULT_TEXT_COLOR);
  const [currentLinkUrl, setCurrentLinkUrl] = React.useState('');
  const [selectionHasText, setSelectionHasText] = React.useState(false);
  const [listType, setListType] = React.useState<
    'bullet' | 'check' | 'number' | null
  >(null);
  const [textAlignment, setTextAlignment] =
    React.useState<ElementFormatType>('left');
  const savedSelectionRef = React.useRef<RangeSelection | null>(null);

  React.useEffect(() => {
    const readToolbarState = () => {
      editor.getEditorState().read(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          return;
        }

        setBlockType(getSelectedBlockType(selection));
        setFontFamily(
          $getSelectionStyleValueForProperty(
            selection,
            'font-family',
            DEFAULT_FONT_FAMILY
          ) || DEFAULT_FONT_FAMILY
        );
        setFontSize(
          (
            $getSelectionStyleValueForProperty(
              selection,
              'font-size',
              DEFAULT_FONT_SIZE
            ) || DEFAULT_FONT_SIZE
          ).replace('px', '')
        );
        setIsBold(selection.hasFormat('bold'));
        setIsItalic(selection.hasFormat('italic'));
        setIsUnderline(selection.hasFormat('underline'));
        setIsStrikethrough(selection.hasFormat('strikethrough'));
        setCurrentLinkUrl(getSelectedLinkUrl(selection));

        if (!isLinkEditorVisible) {
          savedSelectionRef.current = selection.clone();
          setSelectionHasText(selection.getTextContent().trim() !== '');
        }

        const anchorNode = selection.anchor.getNode();
        const parentList = $findMatchingParent(anchorNode, $isListNode);
        setListType(parentList?.getListType() ?? null);

        setTextColor(
          $getSelectionStyleValueForProperty(selection, 'color', '') || ''
        );
        setTextBackgroundColor(
          $getSelectionStyleValueForProperty(
            selection,
            'background-color',
            DEFAULT_BACKGROUND_COLOR
          ) || DEFAULT_BACKGROUND_COLOR
        );

        const selectedElement = getSelectedTopLevelElement(selection);
        setTextAlignment(selectedElement?.getFormatType() || 'left');
      });
    };

    readToolbarState();

    return mergeRegister(
      editor.registerUpdateListener(() => {
        readToolbarState();
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          readToolbarState();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, isLinkEditorVisible]);

  const applyBlockType = React.useCallback(
    (nextBlockType: BlockType | 'number' | 'bullet') => {
      if (nextBlockType === 'bullet') {
        editor.dispatchCommand(
          listType === 'bullet'
            ? REMOVE_LIST_COMMAND
            : INSERT_UNORDERED_LIST_COMMAND,
          undefined
        );
        return;
      }

      if (nextBlockType === 'number') {
        editor.dispatchCommand(
          listType === 'number'
            ? REMOVE_LIST_COMMAND
            : INSERT_ORDERED_LIST_COMMAND,
          undefined
        );
        return;
      }

      if (listType) {
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      }

      editor.update(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          return;
        }

        if (nextBlockType === 'paragraph') {
          $setBlocksType(selection, () => $createParagraphNode());
        }

        if (nextBlockType === 'h1') {
          $setBlocksType(selection, () => $createHeadingNode('h1'));
        }

        if (nextBlockType === 'h2') {
          $setBlocksType(selection, () => $createHeadingNode('h2'));
        }

        if (nextBlockType === 'h3') {
          $setBlocksType(selection, () => $createHeadingNode('h3'));
        }

        if (nextBlockType === 'quote') {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      });
    },
    [editor, listType]
  );

  const applyTextStyle = React.useCallback(
    (style: TextFormatType) => {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, style);
    },
    [editor]
  );

  const applyFontFamily = React.useCallback(
    (nextFontFamily: string) => {
      setFontFamily(nextFontFamily);

      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            'font-family': nextFontFamily,
          });
        }
      });
    },
    [editor]
  );

  const applyFontSize = React.useCallback(
    (nextFontSize: string) => {
      setFontSize(nextFontSize);

      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            'font-size': `${nextFontSize}px`,
          });
        }
      });
    },
    [editor]
  );

  const applyTextColor = React.useCallback(
    (nextColor: string) => {
      setTextColor(nextColor);

      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            color: nextColor || null,
          });
        }
      });
    },
    [editor]
  );

  const applyTextBackgroundColor = React.useCallback(
    (nextColor: string) => {
      const normalizedColor = nextColor || DEFAULT_BACKGROUND_COLOR;
      setTextBackgroundColor(normalizedColor);

      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            'background-color':
              normalizedColor === DEFAULT_BACKGROUND_COLOR
                ? null
                : normalizedColor,
          });
        }
      });
    },
    [editor]
  );

  const clearFormatting = React.useCallback(() => {
    editor.update(() => {
      $clearSelectionFormatting();
    });
  }, [editor]);

  return {
    formatting: {
      applyBlockType,
      applyFontFamily,
      applyFontSize,
      applyTextBackgroundColor,
      applyTextColor,
      applyTextStyle,
      blockType,
      clearFormatting,
      fontFamily,
      fontSize,
      isBold,
      isItalic,
      isStrikethrough,
      isUnderline,
      listType,
      textAlignment,
      textBackgroundColor,
      textColor,
    },
    selection: {
      currentLinkUrl,
      savedSelectionRef,
      selectionHasText,
    },
  };
}
